import { MessageCircle, Phone } from "lucide-react";
import PageHead from "../components/PageHead";
import { Reveal, SectionHead } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { DEFAULT_WA, IMAGES, SITE } from "../lib/site";

const SERVICES = [
    {
        no: "01",
        title: "Pan India Trailer Service",
        hindi: "Trailer service across Pan India",
        desc: "Dedicated trailer supply on every major lane — Mumbai to Delhi, Gujarat to Kolkata, Chennai to Nagpur and beyond. One supplier for the whole map.",
        points: ["All-India lanes", "Dedicated full-truck loads", "Door-to-door placement"],
        img: IMAGES.fleet,
        alt: "Trailer fleet on a long-distance highway run",
    },
    {
        no: "02",
        title: "ODC & Loose Cargo Transportation",
        hindi: "ODC & loose cargo movement",
        desc: "Over-dimensional and loose cargo needs the right trailer, the right route and the right handling. We plan all three before the wheels turn.",
        points: ["Over-dimensional cargo (ODC)", "Heavy machinery & equipment", "Break-bulk & loose cargo"],
        img: IMAGES.crane,
        alt: "Heavy cargo being lifted at an industrial site",
    },
    {
        no: "03",
        title: "Trailer Availability",
        hindi: "Trailers available on demand",
        desc: "40ft, 50ft and 60ft trailers available across different locations in India. Call, WhatsApp or drop an enquiry — we place a vehicle fast.",
        points: ["40ft / 50ft / 60ft trailers", "Placement across Indian hubs", "On-call availability, 24×7"],
        img: IMAGES.flatbed,
        alt: "Flatbed trailer ready for loading",
    },
    {
        no: "04",
        title: "Long-Distance Transportation",
        hindi: "Long-distance haulage",
        desc: "Inter-state haulage with committed timelines. Loaded on time, moved on the right corridor, delivered without drama.",
        points: ["Inter-state haulage", "Committed transit timelines", "Corridor expertise"],
        img: IMAGES.twilight,
        alt: "Truck on the highway at dusk",
    },
];

const STEPS = [
    { no: "1", title: "Share your route", hindi: "Call, WhatsApp or form" },
    { no: "2", title: "Get a competitive rate", hindi: "Fair & upfront" },
    { no: "3", title: "Vehicle is placed", hindi: "40ft, 50ft or 60ft" },
    { no: "4", title: "Loaded & moved on time", hindi: "Delivered without drama" },
];

export default function Services() {
    return (
        <>
            <PageHead
                eyebrow="Our Services"
                title="Trailers, cargo & corridors"
                hindi="Our services — across India"
                intro="Four services, one standard: the right trailer at the right price, placed on time and moved professionally."
                testid="services-page"
            />

            <section className="pb-20 lg:pb-28" data-testid="services-list-section">
                <div className="container-x space-y-20 lg:space-y-28">
                    {SERVICES.map((s, i) => (
                        <Reveal key={s.no}>
                            <div
                                className={`grid items-center gap-10 lg:grid-cols-12 ${
                                    i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                                }`}
                                data-testid={`service-block-${s.no}`}
                            >
                                <div className="lg:col-span-5">
                                    <div className="group overflow-hidden rounded-3xl">
                                        <img
                                            src={s.img}
                                            alt={s.alt}
                                            loading="lazy"
                                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                    </div>
                                </div>
                                <div className="lg:col-span-7">
                                    <p className="font-mono text-sm font-semibold text-brand">{s.no}</p>
                                    <h2 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-ink sm:text-4xl">
                                        {s.title}
                                    </h2>
                                    <p className="mt-2 font-hindi text-base text-steel">{s.hindi}</p>
                                    <p className="mt-4 max-w-xl text-base leading-relaxed text-steel">
                                        {s.desc}
                                    </p>
                                    <ul className="mt-6 flex flex-wrap gap-2">
                                        {s.points.map((p) => (
                                            <li
                                                key={p}
                                                className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink"
                                            >
                                                {p}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-7 flex flex-wrap gap-3">
                                        <a
                                            href={`tel:${SITE.phones[0].tel}`}
                                            data-testid={`service-call-btn-${s.no}`}
                                            className="inline-flex items-center gap-2 rounded-full border-2 border-ink px-5 py-2.5 text-sm font-semibold text-ink transition-all hover:bg-ink hover:text-paper"
                                        >
                                            <Phone className="h-4 w-4" /> Call to book
                                        </a>
                                        <a
                                            href={DEFAULT_WA}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`service-whatsapp-btn-${s.no}`}
                                            className="inline-flex items-center gap-2 rounded-full bg-[#1FA855] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#178a45]"
                                        >
                                            <MessageCircle className="h-4 w-4" /> WhatsApp
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-sand py-20 lg:py-28" data-testid="services-process-section">
                <div className="container-x">
                    <SectionHead
                        eyebrow="How It Works"
                        title="Four steps, zero guesswork"
                        hindi="Four easy steps"
                    />
                    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.no} delay={i * 0.07} className="h-full">
                                <div className="card flex h-full flex-col p-6">
                                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand font-display text-base font-bold text-white">
                                        {s.no}
                                    </span>
                                    <p className="mt-5 font-display text-lg font-bold uppercase tracking-tight text-ink">
                                        {s.title}
                                    </p>
                                    <p className="mt-1 font-hindi text-sm text-steel">{s.hindi}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand title="Ready to book a trailer?" hindi="Booking a trailer? One call away." />
        </>
    );
}
